/** React side of `time-zone.ts`: the shell applies the zone, Settings reads and changes it. */
import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";

import { portal } from "./api";
import {
  adoptStoredTimeZone,
  displayTimeZone,
  loadTimeZoneOverride,
  saveTimeZoneOverride,
  setBusinessTimeZone,
  subscribeTimeZone,
} from "./time-zone";

/** The business profile (time zone, industry, currency); Settings → Business edits the same cache entry. */
export function useBusinessProfile() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: ["business-profile", orgId],
    queryFn: () => portal.profile(orgId),
    staleTime: 5 * 60_000,
  });
}

/**
 * Called by `AppShell`: keeps the formatters' zone in step with the business profile and this person's
 * saved choice, and returns it so the shell can remount the page when it changes. The zone is applied
 * while rendering, so the page below is written in the right zone from its first paint.
 */
export function useApplyDisplayTimeZone(): string {
  const user = useUser();
  const profile = useBusinessProfile();
  adoptStoredTimeZone(user.id);
  setBusinessTimeZone(profile.data?.time_zone);
  return useSyncExternalStore(subscribeTimeZone, displayTimeZone);
}

/** What Settings shows and changes: the zone times are written in, and this person's override of it. */
export function useTimeZoneChoice() {
  const user = useUser();
  const profile = useBusinessProfile();
  const zone = useSyncExternalStore(subscribeTimeZone, displayTimeZone);
  const override = loadTimeZoneOverride(user.id);
  return {
    zone,
    businessZone: profile.data?.time_zone ?? null,
    /** This person's own zone, or null when they follow the business. */
    override,
    /** A zone to use from now on, or null to follow the business again. */
    choose: (next: string | null) => saveTimeZoneOverride(user.id, next),
  };
}
